import React, { useState, useEffect, useContext } from "react";
import { Table, Button, Badge, Card, Pagination, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom"; 
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api"; 
import UserContext from "../../context/UserContext";

const OTApproval = () => {
  const { user } = useContext(UserContext); 
  const navigate = useNavigate(); 
  const [pendingOT, setPendingOT] = useState([]); 
  const [loading, setLoading] = useState(false);

  // --- PAGINATION CONFIGURATION ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Đảm bảo 10 dòng trên 1 trang

  const getUserId = () => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.id || user?.userId || storedUser?.id || storedUser?.userId;
  };

  useEffect(() => {
    fetchPendingOT();
  }, [user]);

  const fetchPendingOT = async () => {
    setLoading(true);
    try {
      const currentId = getUserId();
      if (!currentId) return;

      const res = await axiosClient.get(`${API_ENDPOINTS.otRequests}/Notifications/${currentId}`); 
      // Lọc các đơn chưa có kết quả cuối cùng (đang chờ duyệt)
      const onlyPending = res.data.filter(item => !item.isFinalResult);
      setPendingOT(onlyPending);
      
      // Reset về trang 1 khi load lại dữ liệu
      setCurrentPage(1);
    } catch (err) { 
      console.error("Error loading OT approvals:", err); 
    } finally {
      setLoading(false);
    }
  };

  // --- LOGIC TÍNH TOÁN PHÂN TRANG ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = pendingOT.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(pendingOT.length / itemsPerPage);

  // Hàm thay đổi trang
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold text-dark text-uppercase mb-0">OT Approval Portal</h3>
          <p className="text-muted small mb-0">Manage and review overtime requests from employees</p>
        </div>
        <Button variant="primary" size="sm" onClick={fetchPendingOT} disabled={loading}>
           <i className={`fas fa-sync-alt me-1 ${loading ? 'fa-spin' : ''}`}></i> Refresh
        </Button>
      </div>

      <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
        <Card.Header className="bg-white py-3 border-bottom border-light">
            <h5 className="mb-0 fw-bold text-primary d-flex align-items-center">
              <i className="fas fa-tasks me-2"></i>Pending Requests
            </h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive className="align-middle mb-0">
            <thead style={{ backgroundColor: "#f1f4f9" }}>
              <tr>
                <th className="ps-4 py-3 text-muted fw-bold small text-uppercase">Request Details</th>
                <th className="py-3 text-muted fw-bold small text-uppercase text-center">Status</th>
                <th className="py-3 text-muted fw-bold small text-end pe-4 text-uppercase">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="3" className="text-center py-5">
                    <Spinner animation="border" size="sm" variant="primary" /> <span className="ms-2">Loading data...</span>
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <tr key={item.id} className="border-bottom border-light">
                    <td className="ps-4 py-3">
                      <div className="fw-bold text-dark">{item.message}</div>
                      <div className="text-muted small">Request ID: #OT-{item.id}</div>
                    </td>
                    <td className="text-center">
                      <Badge bg="warning" text="dark" className="px-3 py-2 rounded-pill fw-normal">
                          <i className="far fa-clock me-1"></i> PENDING
                      </Badge>
                    </td>
                    <td className="text-end pe-4">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        className="rounded-pill px-4 shadow-sm fw-bold"
                        
                        onClick={() => navigate(`/ot-approval-detail/${item.id}`)} 
                        
                      >
                        Process Now
                      </Button>
                    </td>
                  </tr>
                ))
              ) : null}
            </tbody>
          </Table>

          {/* HIỂN THỊ KHI TRỐNG DỮ LIỆU */}
          {!loading && pendingOT.length === 0 && (
            <div className="text-center py-5 bg-white">
               <i className="fas fa-check-circle text-success mb-3" style={{ fontSize: '2.5rem', opacity: 0.3 }}></i>
               <h6 className="text-muted fw-normal">Great! You have no pending requests to approve.</h6>
            </div>
          )}

          {/* FOOTER PHÂN TRANG */}
          {!loading && pendingOT.length > 0 && (
            <div className="d-flex justify-content-between align-items-center px-4 py-3 bg-light border-top">
              <span className="text-muted small">
                Showing <strong>{indexOfFirstItem + 1}</strong> - <strong>{Math.min(indexOfLastItem, pendingOT.length)}</strong> of <strong>{pendingOT.length}</strong>
              </span>
              
              {totalPages > 1 && (
                <Pagination className="mb-0 shadow-sm">
                  <Pagination.Prev 
                    disabled={currentPage === 1} 
                    onClick={() => paginate(currentPage - 1)} 
                  />
                  
                  {[...Array(totalPages)].map((_, i) => (
                    <Pagination.Item 
                      key={i + 1} 
                      active={i + 1 === currentPage} 
                      onClick={() => paginate(i + 1)}
                    >
                      {i + 1}
                    </Pagination.Item>
                  ))}
                  
                  <Pagination.Next 
                    disabled={currentPage === totalPages} 
                    onClick={() => paginate(currentPage + 1)} 
                  />
                </Pagination>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default OTApproval;